/**
 * Emoji are rendered from bundled PNG artwork instead of system emoji fonts,
 * so every user sees the exact same emoji on every platform.
 *
 * Assets live in `packages/frontend/core/public/emoji/apple/64/` (Apple set
 * from `emoji-datasource-apple`, internal use only). Documents keep storing
 * plain unicode — only the rendering changes.
 *
 * Filename convention: every code point as lowercase hex, zero-padded to 4
 * chars, joined with '-' (e.g. "👩‍💻" -> "1f469-200d-1f4bb.png"). FE0F-stripped
 * variants are pre-aliased at asset-copy time, so a single direct URL is
 * enough at runtime.
 *
 * This module must stay free of React/JSX/CSS imports: it is loaded from
 * `bootstrap/public-path.ts`, which is also bundled into web workers.
 */

let emojiAssetBase = '/emoji/apple/64/';

export function configureEmojiAssets(baseUrl: string) {
  emojiAssetBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
}

export function emojiToFilename(unicode: string): string {
  return Array.from(unicode)
    .map(char => (char.codePointAt(0) ?? 0).toString(16).padStart(4, '0'))
    .join('-');
}

export function getEmojiFileUrl(filename: string): string {
  return `${emojiAssetBase}${filename}.png`;
}

export function getEmojiArtworkUrl(unicode: string): string {
  return getEmojiFileUrl(emojiToFilename(unicode));
}

// Remembers artwork that failed to load so remounts of the same emoji skip
// the broken request and go straight to the text fallback.
const missingArtwork = new Set<string>();

export function isEmojiArtworkMissing(filename: string): boolean {
  return missingArtwork.has(filename);
}

export function markEmojiArtworkMissing(filename: string) {
  missingArtwork.add(filename);
}
